/**
 * Domain types for the `project-member` entity slice.
 *
 * Public surface: see `entities/project-member/index.ts`.
 *
 * Migrated from `entities/project/model/types.ts` to keep these types
 * co-located with the queryOptions helpers and UI primitives that consume
 * them.
 */

/** A role that can be assigned at project scope, fetched from the platform API. */
export interface PlatformRole {
  id: string;
  roleName: string;
  description: string | null;
  kind: 'system' | 'org_specific';
  ownerSlug: string;
  permissions: string[];
  validAssignmentScopes: string[];
}

/** A role the members panel offers in a project, and whether the caller may assign it there. */
export interface AssignableProjectRole extends PlatformRole {
  assignable: boolean;
}

/** A role assignment for a user within a scope, fetched from the platform API. */
export interface RoleAssignment {
  id: string;
  identityId: string;
  roleId: string;
  roleName: string;
  ownerSlug: string;
  scopeKind: 'org' | 'workspace' | 'project';
  scopeId: string;
  scopeName?: string;
  parentWorkspaceId?: string;
  createdAt: string;
}

// eslint-disable-next-line i18next/no-literal-string -- compared against `role.roleName` from the API
export const DEFAULT_PROJECT_ROLE_NAME = 'Contributor';

/**
 * A user with explicit project membership (avatar/email + permissions array).
 * Returned by `GET /workspaces/:wsId/projects/:projectId/users`.
 */
export interface ProjectMember {
  id: string;
  name: string;
  email: string;
  permissions: string[];
  avatarUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * A user under a workspace (potentially not a member of any project).
 * Used to power the AddMembersSection picker.
 */
export interface WorkspaceUser {
  id: string;
  name: string;
  email?: string;
  avatarUrl?: string | null;
  roles?: Array<{
    id: string;
    roleName: string;
  }>;
}

export interface ProjectMembersResponse {
  data: ProjectMember[];
}

export interface WorkspaceUsersResponse {
  data: WorkspaceUser[];
}
